import { MapPin, Phone, ArrowUpRight, CheckCircle2, Truck, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { PageHero } from "../components/PageHero";
import { PrairieMap } from "../components/PrairieMap";
import { BUSINESS, SERVICE_AREAS } from "../lib/content";

const AREA_META = {
  "Medicine Hat": {
    sub: "Southeast Alberta",
    population: "65K+",
    popLabel: "City Population",
    coverage: "Residential · Commercial · Industrial",
    image:
      "https://images.unsplash.com/photo-1772852340517-b11ec88979fc?fm=jpg&q=80&w=1600&auto=format&fit=crop",
    highlights: [
      "Driveway paving & reconstruction",
      "Commercial parking lots",
      "Industrial site paving",
    ],
  },
  Regina: {
    sub: "Capital of Saskatchewan",
    population: "230K+",
    popLabel: "Metro Population",
    coverage: "Residential · Commercial · Municipal",
    image:
      "https://images.unsplash.com/photo-1708117242652-25dc76c4b30c?fm=jpg&q=80&w=1600&auto=format&fit=crop",
    highlights: [
      "Driveways & residential lots",
      "Municipal & commercial contracts",
      "Seal coating & crack sealing",
    ],
  },
  Brandon: {
    sub: "Western Manitoba Hub",
    population: "55K+",
    popLabel: "City Population",
    coverage: "Residential · Rural · Commercial",
    image:
      "https://images.unsplash.com/photo-1772852311587-329719989e2b?fm=jpg&q=80&w=1600&auto=format&fit=crop",
    highlights: [
      "Rural driveways & acreages",
      "Hot-mix asphalt paving",
      "Tar & chip applications",
    ],
  },
  Lloydminster: {
    sub: "Border City · AB / SK",
    population: "32K+",
    popLabel: "City Population",
    coverage: "Residential · Commercial · Oil & Gas",
    image:
      "https://images.unsplash.com/photo-1776381733574-372689e246b4?fm=jpg&q=80&w=1600&auto=format&fit=crop",
    highlights: [
      "Oil-sector site paving",
      "Commercial parking lots",
      "Seal coating & crack sealing",
    ],
  },
};

export default function ServiceArea() {
  return (
    <div data-testid="service-area-page">
      <PageHero
        eyebrow="Service Area"
        title="Serving"
        accent="AB · SK · MB."
        subtitle="From Medicine Hat to Brandon — North West Asphalt covers the prairie heartland of Alberta, Saskatchewan and Manitoba."
        testId="service-area-hero"
      />

      {/* Map + summary band */}
      <section
        className="py-16 md:py-24 bg-[#050505] border-b border-[#27272a]"
        data-testid="service-area-summary"
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Map graphic */}
          <div className="lg:col-span-7 order-2 lg:order-1" data-testid="prairie-map">
            <div className="relative bg-[#09090b] border border-[#27272a] p-4 md:p-6">
              <PrairieMap className="w-full h-auto" />
            </div>
          </div>

          {/* Summary numbers */}
          <div className="lg:col-span-5 order-1 lg:order-2 grid grid-cols-2 gap-4">
            <div className="border border-[#27272a] bg-[#09090b] p-6">
              <MapPin className="w-5 h-5 text-[#f59e0b] mb-3" />
              <div className="font-display font-black text-4xl text-[#fafaf9] leading-none mb-2">
                4
              </div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#a1a1aa]">
                Service Cities
              </div>
            </div>
            <div className="border border-[#27272a] bg-[#09090b] p-6">
              <Truck className="w-5 h-5 text-[#f59e0b] mb-3" />
              <div className="font-display font-black text-4xl text-[#fafaf9] leading-none mb-2">
                3<span className="text-[#f59e0b]"></span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#a1a1aa]">
                Provinces · AB SK MB
              </div>
            </div>
            <div className="border border-[#27272a] bg-[#09090b] p-6">
              <Users className="w-5 h-5 text-[#f59e0b] mb-3" />
              <div className="font-display font-black text-4xl text-[#fafaf9] leading-none mb-2">
                1.8<span className="text-[#f59e0b]">K+</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#a1a1aa]">
                Projects Completed
              </div>
            </div>
            <div className="border border-[#27272a] bg-[#09090b] p-6">
              <CheckCircle2 className="w-5 h-5 text-[#f59e0b] mb-3" />
              <div className="font-display font-black text-4xl text-[#fafaf9] leading-none mb-2">
                40<span className="text-[#f59e0b]">+</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#a1a1aa]">
                Years In Region
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Regional cards */}
      <section className="py-24 md:py-32" data-testid="service-area-regions">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="flex items-end justify-between mb-14 gap-6 flex-wrap">
            <div>
              <div className="label-sm text-[#f59e0b] mb-5">Our regions</div>
              <h2 className="font-display font-black text-4xl sm:text-5xl md:text-6xl uppercase leading-[0.9]">
                Four cities.
                <br />
                <span className="text-[#f59e0b]">One promise.</span>
              </h2>
            </div>
            <p className="text-[#a1a1aa] max-w-md">
              Every regional city is served with the same disciplined paving
              standard — residential, commercial, industrial and municipal.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICE_AREAS.map((a, i) => {
              const meta = AREA_META[a.city] || {};
              return (
                <div
                  key={i}
                  className="group relative bg-[#18181b] border border-[#27272a] hover:border-[#f59e0b] transition-all duration-500 overflow-hidden flex flex-col"
                  data-testid={`area-card-${i}`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={meta.image}
                      alt={`${a.city} paving`}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#09090b] via-[#09090b]/30 to-transparent" />
                    <div className="absolute top-3 left-3 w-9 h-9 bg-[#f59e0b] text-[#09090b] flex items-center justify-center font-display font-black text-xs">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-[#09090b]/80 backdrop-blur px-2.5 py-1 border border-[#27272a]">
                      <span className="text-[10px] uppercase tracking-[0.25em] text-[#f59e0b] font-bold">
                        {a.province}
                      </span>
                    </div>
                    <div className="absolute bottom-3 left-4 right-4">
                      <div className="text-[9px] uppercase tracking-[0.3em] text-[#f59e0b] font-bold mb-1">
                        {meta.sub}
                      </div>
                      <div className="font-display font-black text-2xl md:text-3xl uppercase leading-none text-[#fafaf9]">
                        {a.city}
                      </div>
                    </div>
                  </div>

                  <div className="p-6 flex-1 flex flex-col">
                    <div className="pb-4 border-b border-[#27272a] mb-4">
                      <div className="text-[9px] uppercase tracking-[0.25em] text-[#a1a1aa] mb-1">
                        Region
                      </div>
                      <div className="font-bold text-[#fafaf9] text-sm">
                        {a.region}
                      </div>
                    </div>

                    <p className="text-[#d4d4d8] text-sm leading-relaxed mb-5">
                      {a.note}
                    </p>

                    <div className="mt-auto">
                      <div className="text-[10px] uppercase tracking-[0.3em] text-[#f59e0b] font-bold mb-3">
                        What we cover
                      </div>
                      <ul className="space-y-2 mb-5">
                        {meta.highlights?.map((h, j) => (
                          <li
                            key={j}
                            className="flex items-start gap-2.5 text-xs text-[#fafaf9]"
                          >
                            <CheckCircle2
                              className="w-3.5 h-3.5 text-[#f59e0b] mt-0.5 shrink-0"
                              strokeWidth={2.5}
                            />
                            <span>{h}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <a
                    href={`tel:${BUSINESS.phoneRaw}`}
                    data-testid={`area-card-${i}-call`}
                    className="flex items-center justify-between px-6 py-4 border-t border-[#27272a] group-hover:border-[#f59e0b]/40 bg-[#09090b] group-hover:bg-[#f59e0b] transition-colors"
                  >
                    <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#fafaf9] group-hover:text-[#09090b]">
                      Request a quote
                    </span>
                    <ArrowUpRight
                      className="w-4 h-4 text-[#f59e0b] group-hover:text-[#09090b] transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                      strokeWidth={3}
                    />
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Beyond + CTA band */}
      <section
        className="py-24 md:py-28 bg-[#050505] border-y border-[#27272a]"
        data-testid="service-area-beyond"
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="label-sm text-[#f59e0b] mb-5">Beyond our regions</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl md:text-6xl uppercase leading-[0.9] mb-6">
              Larger projects?
              <br />
              <span className="text-[#f59e0b]">We travel.</span>
            </h2>
            <p className="text-[#a1a1aa] text-lg leading-relaxed max-w-xl">
              North West Asphalt extends services throughout the prairies for
              larger commercial, industrial and municipal paving contracts.
              Give us the project details and we'll put together a plan.
            </p>
          </div>
          <div
            className="bg-[#18181b] border border-[#27272a] p-8 md:p-10 relative overflow-hidden"
            data-testid="area-contact-card"
          >
            <div className="absolute -top-10 -right-10 w-52 h-52 rounded-full bg-[#f59e0b]/10 blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="label-sm text-[#f59e0b] mb-5">Ready to start?</div>
              <div className="font-display font-black text-3xl md:text-4xl uppercase mb-6 leading-tight">
                Call us for a<br />
                site visit & written quote.
              </div>
              <div className="space-y-3 mb-8 text-[#fafaf9]">
                <a
                  href={`tel:${BUSINESS.phoneRaw}`}
                  className="flex items-center gap-3 hover:text-[#f59e0b] transition-colors text-lg"
                  data-testid="area-phone-link"
                >
                  <Phone className="w-5 h-5 text-[#f59e0b]" strokeWidth={3} />
                  <span className="font-bold">{BUSINESS.phone}</span>
                </a>
                <a
                  href={`tel:${BUSINESS.phone2Raw}`}
                  className="flex items-center gap-3 hover:text-[#f59e0b] transition-colors text-lg"
                  data-testid="area-phone-link-2"
                >
                  <Phone className="w-5 h-5 text-[#f59e0b]" strokeWidth={3} />
                  <span className="font-bold">{BUSINESS.phone2}</span>
                </a>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href={`tel:${BUSINESS.phoneRaw}`}
                  className="inline-flex items-center gap-3 bg-[#f59e0b] hover:bg-[#d97706] text-[#09090b] font-bold px-7 py-4 text-xs uppercase tracking-[0.25em] transition-colors"
                  data-testid="area-call-btn"
                >
                  <Phone className="w-4 h-4" strokeWidth={3} />
                  Call us
                </a>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-3 border border-[#3f3f46] hover:border-[#f59e0b] text-[#fafaf9] font-bold px-7 py-4 text-xs uppercase tracking-[0.25em] transition-colors"
                  data-testid="area-contact-cta"
                >
                  Contact page
                  <ArrowUpRight className="w-4 h-4" strokeWidth={3} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
